import { useQuery as useProductBestImageQuery } from "@faire/web-api/api/product/productToken/best-image/get-hooks";
import { useQuery as useSEOSignUpModalQuery } from "@faire/web-api/api/seo/sign-up-modal/get-hooks";
import { trackFaireDirectShoppableLandingPageModalCloseClick } from "@faire/web-api/events/faireDirect/click/shoppableLandingPageModalClose";
import { trackSignupCloseSignUpModalClick } from "@faire/web-api/events/signup/click/closeSignUpModal";
import { QueryParams } from "@faire/web/common/query-params/QueryParams";
import { Storage } from "@faire/web/common/Storage";
import qs from "qs";
import { useCallback, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { recordSignupActivity } from "@faire/retailer/actions/user/recordLoggedOutActivity";
import { useFaireDirectBrand } from "@faire/retailer/hooks/FaireDirect/useFaireDirectBrand/useFaireDirectBrand";

import {
  notifySignupSnackbarEvent,
  SignUpSnackbarEvent,
} from "../../LoggedOutMobileSignUpSnackbar/useSignupSnackbarStateMachine";
import {
  isFaireDirectVariant,
  isAutoSignUpVariant,
  determineAppSignUpModalVariant,
  AppSignUpModalVariant,
} from "../AppSignUpModalVariant";
import { getSignUpParams } from "../AppSignUpQueryParams";

export const HAS_CLOSED_SIGNUP_MODAL = "has-closed-signup-modal";

const EMPTY_QUERY_FIELDS = {
  signUp: undefined,
  product: undefined,
  brand: undefined,
  video: undefined,
  source: undefined,
  email: undefined,
  refB: undefined,
  refC: undefined,
  refF: undefined,
  refS: undefined,
  refOrigin: undefined,
  faireMarket: undefined,
};

export const useHandleModalState = (queryParams: QueryParams) => {
  const history = useHistory();
  const location = useLocation();
  const { faireDirectBrandToken } = useFaireDirectBrand();

  const signUpParams = getSignUpParams(queryParams);

  const modalVariant = useMemo(
    () => determineAppSignUpModalVariant(queryParams, location),
    [location, queryParams]
  );

  const {
    data: signUpModalProductImageResponse,
    isPending: isSEOSignUpModalQueryPending,
    refetch: fetchSignUpModalProductImage,
  } = useSEOSignUpModalQuery(undefined, {
    enabled: false,
  });

  const {
    data: productBestImageResponse,
    isPending: isProductBestImageQueryPending,
    refetch: fetchProductBestImageQuery,
  } = useProductBestImageQuery(
    modalVariant === AppSignUpModalVariant.FACEBOOK
      ? queryParams.getOrDefault("refP", "")
      : queryParams.getOrDefault("product", ""),
    {
      enabled: false,
    }
  );

  const handleModalClose = useCallback(() => {
    history.replace({
      ...history.location,
      search: qs.stringify({
        ...queryParams,
        ...EMPTY_QUERY_FIELDS,
      }),
    });

    if (isFaireDirectVariant(modalVariant)) {
      trackFaireDirectShoppableLandingPageModalCloseClick();
    } else {
      trackSignupCloseSignUpModalClick();
    }

    if (isAutoSignUpVariant(modalVariant)) {
      notifySignupSnackbarEvent(SignUpSnackbarEvent.AUTO_SIGNUP_CLOSED);
    }

    Storage.session.setItem(HAS_CLOSED_SIGNUP_MODAL, "true");
  }, [history, modalVariant, queryParams]);

  const recordModalSignUpActivity = useCallback(() => {
    // For leveraging logged out signals from product signups
    if (modalVariant === AppSignUpModalVariant.PRODUCT) {
      recordSignupActivity(
        {
          product_token: signUpParams.product,
          brand_token: signUpParams.brand,
        },
        location.pathname
      );
    } else if (isFaireDirectVariant(modalVariant)) {
      recordSignupActivity(
        {
          brand_token: faireDirectBrandToken,
        },
        location.pathname
      );
    }
  }, [
    faireDirectBrandToken,
    location.pathname,
    modalVariant,
    signUpParams.brand,
    signUpParams.product,
  ]);

  return {
    modalVariant,
    signUpModalProductImageResponse,
    productBestImageResponse,
    isFeaturedImageLoading:
      isSEOSignUpModalQueryPending || isProductBestImageQueryPending,
    fetchSignUpModalProductImage,
    fetchProductBestImageQuery,
    handleModalClose,
    recordModalSignUpActivity,
  };
};
